import {useLiveQuery} from 'dexie-react-hooks';
import {offlineDb} from 'src/offline/db';
import type {OfflineSession, OutboxItem} from 'src/offline/types';

export interface OfflineSnapshotSummary {
  key: string;
  cachedAt: string;
}

export interface OfflineTestData {
  activeSession: OfflineSession | undefined;
  outboxItems: OutboxItem[];
  snapshots: OfflineSnapshotSummary[];
}

/**
 * Reactive view of the active offline session, its outbox, and its hydration
 * snapshots — backed by Dexie's `useLiveQuery` so the Offline Test page
 * updates immediately as `src/offline/*` mutates IndexedDB.
 */
export const useOfflineTestData = (): OfflineTestData => {
  const activeSession = useLiveQuery(
    () => offlineDb.sessions.where('status').equals('active').first(),
    []
  );

  const sessionId = activeSession?.id;

  const outboxItems =
    useLiveQuery(async () => {
      if (!sessionId) return [];
      return offlineDb.outbox.where('sessionId').equals(sessionId).sortBy('seq');
    }, [sessionId]) ?? [];

  const snapshots =
    useLiveQuery(async () => {
      if (!sessionId) return [];
      const rows = await offlineDb.snapshots
        .where('sessionId')
        .equals(sessionId)
        .toArray();
      return rows.map(row => ({key: row.key, cachedAt: row.cachedAt}));
    }, [sessionId]) ?? [];

  return {activeSession, outboxItems, snapshots};
};
